import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { Observable, of, Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { CustomerAuthService } from 'src/app/features/customer-submission/services/customer-auth.service';
import { INotification } from '../../models/i-notification.modal';
import { NotificationsService } from '../../services/notifications.service';
import { UserService } from '../../services/user.service';
import { CompanyService } from '../../services/company.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  public notificationSubscription = Subscription.EMPTY;
  public userSubscription = Subscription.EMPTY;
  public notifications: INotification[] = [];
  public userAccount: AccountInfo;
  contactName$: Observable<string>;
  unreadNotificationCount = 0;
  currentRoute = '';
  buttonText = 'Dashboard';
  isUserLoggedIn = false;
  userDetailsFetched = false;
  showMsHeader = false;
  isMobile: boolean = false;

  constructor(
    public notificationsService: NotificationsService,
    public userService: UserService,
    private router: Router,
    public authService: MsalService,
    public customerAuthService: CustomerAuthService,
    public companyService: CompanyService,
    private cdr: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects;
        this.updateButtonText();
        this.getUserNameDetails();
      }
    });
  }

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobile = result.matches;
    });
    this.userSubscription = this.userService?.userAccount?.subscribe({
      next: (account) => {
        if (account) {
          this.userAccount = account;
          this.fetchNotifications();
        } else {
          this.showMsHeader = false;
        }
      },
    });
  }

  getUserNameDetails() {
    if (
      this.currentRoute.includes('customer-submission') || 
      (this.currentRoute.includes('login') || 
      this.currentRoute.includes('register')
    )) {
      this.showMsHeader = false;
    } else {
      this.showMsHeader = true;
    }
    if (!(this.currentRoute.includes('login') || this.currentRoute.includes('register') || this.currentRoute === '/customer-submission') && !this.showMsHeader) {
      if (this.customerAuthService.isLoggedIn()) {
        this.companyService.getUserDetails().subscribe({
          next: (response: { contactName: string }) => {
            if (response?.contactName) {
              this.userDetailsFetched = true;
              this.contactName$ = of(response.contactName);
              this.cdr.detectChanges();
            }
          },
        });
      }
    }
  }

  updateButtonText() {
    if (this.currentRoute.includes('in-house-dashboard')) {
      this.buttonText = 'Dashboard';
    } else if (this.currentRoute.includes('dashboard')) {
      this.buttonText = 'In-House Dashboard';
    } else {
      this.buttonText = 'In-House Dashboard';
    }
  }

  onNotificationDismiss(notification: INotification): void {
    this.notificationSubscription = this.notificationsService?.markNotificationAsRead(notification)?.subscribe({
      next: () => {
        this.fetchNotifications();
      },
    });
  }

  mixDesign() {
    this.router.navigateByUrl('mix-design');
  }

  public fetchNotifications() {
    this.notificationSubscription = this.notificationsService?.fetchNotifications()?.subscribe({
      next: (response: INotification[]) => {
        this.unreadNotificationCount = response?.filter((item) => !item?.isRead)?.length;
        this.notifications = response?.sort((a, b) => Number(a?.isRead) - Number(b?.isRead));
      },
    });
  }

  ngOnDestroy(): void {
    this.notificationSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
  }

  settings() {
    this.router.navigateByUrl('admin/settings');
  }

  navigateToGeoXplore() {
    this.router.navigateByUrl('geoxplore');
  }

  navigateToGeoXplorePrecast() {
    this.router.navigateByUrl('geoxplore-precast');
  }

  management() {
    this.router.navigateByUrl('admin/access-management');
  }

  companyManagement() {
    this.router.navigateByUrl('admin/company-management');
  }

  disposeSamples() {
    this.router.navigateByUrl('admin/dispose-samples');
  }

  navigateToInHouseDashborad() {
    if (this.currentRoute.includes('in-house-dashboard')) {
      this.router.navigateByUrl('dashboard');
    } else if (this.currentRoute.includes('dashboard')) {
      this.router.navigateByUrl('in-house-dashboard');
    } else {
      this.router.navigateByUrl('in-house-dashboard');
    }
  }

  navigateToAuditLogReport() {
    this.router.navigateByUrl('audit-log-report');
  }

  navigateToAnalyzeTr() {
    this.router.navigateByUrl('analyze-tr');
  }

  reDirectUrl() {
    if (this.currentRoute.includes('dashboard')) {
      window.location.reload();
    } else if (this.currentRoute.includes('customer-submission')) {
      if (this.currentRoute.includes('login') || this.currentRoute.includes('register')) {
        this.router.navigateByUrl('customer-submission/login');
      } else {
        this.router.navigateByUrl('/customer-submission/dashboard');
      }
    } else if (this.currentRoute.includes('customer')) {
      this.router.navigateByUrl('/customer-submission/dashboard');
    } else if (localStorage.getItem('jwtToken')) {
      this.router.navigateByUrl('/customer-submission/dashboard');
    } else {
      this.router.navigateByUrl('dashboard');
    }
  }

  isNoPointerRoute(): boolean {
    const currentUrl = this.router.url;
    return currentUrl === '/customer-submission/login' || currentUrl === '/customer-submission';
  }

  logOut() {
    sessionStorage.clear();
    this.router
      .navigateByUrl('dashboard')
      .then(() => {
        this.authService.logoutRedirect();
      })
      .finally(() => { });
  }

  profile() {
    this.router.navigateByUrl('customer-submission/profile');
  }

  cLogOut() {
    localStorage.removeItem('jwtToken');
    this.userDetailsFetched = false;
    window.location.reload();
    window.onload = () => {
      this.router.navigateByUrl('customer-submission/login');
    };
  }
}
